import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useLoginMutation } from "../../features/auth/authApi";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  getTenantId,
  notifyAuthSessionChanged,
  saveAuthSession,
} from "../../services/authSession";
import { resetDataApiCache } from "../../app/apiCache";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [login, { isLoading }] = useLoginMutation();

  function togglePasswordVisibility() {
    setIsPasswordVisible((prevState) => !prevState);
  }

  const getLoginErrorMessage = (err) =>
    err?.data?.message ||
    err?.data?.detail ||
    err?.data?.error ||
    err?.message ||
    "Login failed";

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginError("");

    const trimmedEmail = email.trim();

    if (!trimmedEmail || !password) {
      setLoginError("Email and password are required.");
      return;
    }

    if (!getTenantId()) {
      setLoginError("Tenant ID is not configured.");
      return;
    }

    try {
      const result = await login({
        email: trimmedEmail,
        password,
      }).unwrap();

      if (!result?.jwtToken || !result?.refreshToken) {
        throw new Error("Login response did not include authentication tokens.");
      }

      saveAuthSession(result);
      resetDataApiCache(dispatch);
      notifyAuthSessionChanged();
      navigate("/setting", { replace: true });
    } catch (err) {
      setLoginError(getLoginErrorMessage(err));
    }
  };

  return (
    <div className="grid md:grid-cols-2 text-lightGrey">
      <div className="w-full h-full overflow-hidden">
        <div className="h-screen bg-primary flex items-center justify-center rounded-tr-2xl rounded-br-2xl">
          <img
            alt="login_image"
            src={`${process.env.PUBLIC_URL}/canteen-login.svg`}
          />
        </div>
      </div>

      <div className="bg-white h-full flex items-center justify-center">
        <div className="w-full p-16">
          <h4 className="text-3xl text-primary font-bold">Log In</h4>

          <form className="py-2" onSubmit={handleLogin}>
            <input
              type="email"
              value={email}
              className="rounded-md border px-3 py-2 my-3 w-full outline-none"
              placeholder="Enter email"
              onChange={(e) => setEmail(e.target.value)}
            />

            <div className="relative py-2">
              <input
                value={password}
                type={isPasswordVisible ? "text" : "password"}
                placeholder="Password"
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 text-base border border-gray-300 rounded-md outline-none"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 flex items-center px-4 text-gray-600"
                onClick={togglePasswordVisibility}
              >
                {isPasswordVisible ? (
                  <Eye className="h-5 w-5" />
                ) : (
                  <EyeOff className="h-5 w-5" />
                )}
              </button>
            </div>

            <p className="text-red-500 font-bold">{loginError}</p>

            <div className="flex items-center justify-center w-full mt-4">
              <button
                disabled={isLoading}
                type="submit"
                className="text-white w-1/4 font-bold bg-primary rounded-md text-center py-2 disabled:opacity-60"
              >
                {isLoading ? "Please Wait..." : "Login"}
              </button>
            </div>
          </form>

          <h6 className="pt-14 text-center text-darkgrey text-sm">
            Powered by ThirdEye AI
          </h6>
        </div>
      </div>
    </div>
  );
};

export default Login;
